/**
 * TriggeringStylingPane.tsx — Component for triggering manuscript styling
 *
 * Feature:
 * - Run styling assistant button (automatically enables tracking changes if not enabled)
 *
 * This component is responsible only for rendering. All business logic
 * is encapsulated in the ProfileProvider's `handleClassifyDocument`.
 */

import React from "react";
import { MessageBar, MessageBarBody, MessageBarTitle, Switch, Tooltip, tokens } from "@fluentui/react-components";
import { useErrorContext, useProfileContext } from "src/providers";
import { formatDuration, formatPerfReport } from "src/services";
import { UnavailableStylesError } from "src/services/errors/style-service-errors";
import {
  LAST_STYLING_RUN_SETTING_KEY,
  removeDocumentSetting,
  saveDocumentSetting,
} from "src/services/settings-service";
import { LoadingOverlay } from "./LoadingOverlay";
import { StatusMessage } from "./StatusMessage";
import { StylingButton } from "./StylingButton";
import { StylingErrorMessage } from "./StylingErrorMessage";
import "./TriggeringStylingPane.css";

import type { ClassificationOutcome } from "src/providers";

interface TriggeringStylingPaneProps {
  onStylingComplete?: () => void;
}

/**
 * Persists the run outcome into the document so a failure still blocks review
 * after a taskpane refresh. `null` clears it for a run that is starting.
 *
 * Best-effort: a settings failure must not be reported as a styling failure,
 * which is what would happen if it propagated into `handleRunStyling`'s catch.
 */
const recordStylingRun = async (outcome: ClassificationOutcome | null): Promise<void> => {
  try {
    await (outcome === null
      ? removeDocumentSetting(LAST_STYLING_RUN_SETTING_KEY)
      : saveDocumentSetting(LAST_STYLING_RUN_SETTING_KEY, outcome));
  } catch (err) {
    console.warn("Failed to record styling run outcome:", err);
  }
};

export const TriggeringStylingPane: React.FC<TriggeringStylingPaneProps> = ({
  onStylingComplete,
}) => {
  const {
    classificationStatus,
    classificationEta,
    isClassifying,
    isRunning,
    progress,
    lockExistingStyles,
    perfReport,
    stagedProfileName,
    commitProfile,
    handleClassifyDocument,
    setLockExistingStyles,
  } = useProfileContext();
  const {
    classificationOutcome,
    classificationWarning,
    clearErrors,
    lastStylingRunFailed,
    setClassificationOutcome,
    setClassificationWarning,
    setLastStylingRunFailed,
    setUnavailableStyles,
    setValidationError,
  } = useErrorContext();

  const handleRunStyling = async () => {
    // Reset every error surface synchronously, so the click batches into one
    // render with no stale error left showing. `commitProfile` clears the
    // status text it owns; awaiting anything first would let the old error
    // paint again while the profile commits.
    clearErrors();
    setLastStylingRunFailed(false);
    try {
      const committedProfileName = await commitProfile();
      if (!committedProfileName) {
        return;
      }
      await recordStylingRun(null);
      const result = await handleClassifyDocument(onStylingComplete)(committedProfileName);
      if (result) {
        setClassificationWarning(result.warning);
        setClassificationOutcome("success");
        await recordStylingRun("success");
      }
    } catch (err) {
      if (err instanceof UnavailableStylesError) {
        setValidationError(err.message);
        setUnavailableStyles(err.unavailableStyles);
      } else {
        setClassificationOutcome("error");
        setLastStylingRunFailed(true);
        await recordStylingRun("error");
      }
    }
  };

  return (
    <div className="triggering-styling-pane-container">
      {isClassifying && classificationStatus && (
        <LoadingOverlay statusMessage={classificationStatus} etaMessage={classificationEta} progress={progress} />
      )}

      {classificationStatus && !isClassifying && (
        <>
          {classificationOutcome === "error" && (
            <StylingErrorMessage statusMessage={classificationStatus} />
          )}
          {classificationOutcome === "success" && (
            <>
              <MessageBar intent="success" shape="rounded" style={{ marginBottom: "3px" }}>
                <MessageBarBody>
                  <MessageBarTitle>Complete</MessageBarTitle>
                  {classificationStatus}
                </MessageBarBody>
              </MessageBar>
              {classificationWarning && (
                <MessageBar intent="warning" shape="rounded">
                  <MessageBarBody>
                    <MessageBarTitle>Not in profile</MessageBarTitle>
                    {classificationWarning}
                  </MessageBarBody>
                </MessageBar>
              )}
            </>
          )}
          {classificationOutcome === null && <StatusMessage message={classificationStatus} />}
        </>
      )}

      {/* After a refresh the run's status text is gone, so the banner above
          cannot render — without this the Review Styles button would be
          disabled with no explanation. */}
      {lastStylingRunFailed && !classificationStatus && !isClassifying && (
        <StylingErrorMessage statusMessage="The last styling run did not finish." />
      )}

      {__MSS_DEBUG_LOGGING__ && perfReport && !isClassifying && (
        <details>
          <summary style={{ cursor: "pointer", fontSize: tokens.fontSizeBase200 }}>
            Timing details ({formatDuration(perfReport.totalMs)})
          </summary>
          <pre
            style={{
              fontSize: tokens.fontSizeBase100,
              userSelect: "text",
              whiteSpace: "pre-wrap",
              overflowX: "auto",
            }}
          >
            {formatPerfReport(perfReport)}
          </pre>
        </details>
      )}

      <div style={{ display: "flex", alignItems: "center", alignSelf: "stretch" }}>
        <Tooltip
          content={{ children: "Preserve recognized styles already applied to the manuscript", className: "tooltip"}}
          relationship="description"
          positioning={"above-end"}
        >
          <Switch
            size="medium"
            label="Maintain existing styles"
            checked={lockExistingStyles}
            onChange={(_ev, data) => setLockExistingStyles(data.checked)}
            disabled={isRunning}
            className="maintain-existing-styles"
          />
        </Tooltip>
      </div>

      <StylingButton
        onClick={handleRunStyling}
        isLoading={isRunning}
        disabled={!stagedProfileName}
      />
    </div>
  );
};
